import { computed, defineComponent, onMounted, reactive } from '@vue/composition-api';
import axios from 'axios';
import moment from 'moment';
import Pagination from '@/components/pagination/pagination.vue';
import { hasRole, isAdmin } from '@/functions/authentication-functions';
import { SettingsContext } from '@/utilities/settings-context';
import { ListArgs } from '../billing/billing-types';
import { AdminInvoiceView, CompanyOrders, FilterPostingOrders, InvoiceStatus, IsPostingOrder, PostingOrder, FilterCandidatePostingOrders, CandidatePackagePostingOrder, OrderType } from './admin-billing-types';
import { InvoiceCreationPeriod } from '@/functions/subscription-functions';
import { formatAmount } from '@/functions/format-functions';
import { alertDialogue, confirmDialogue, generalError } from '@/functions/system-modal-functions';
import { ModalIconType } from '@/components/system-modals/system-modals';
import { i18n } from '@/utilities/vue-i18n';
import { DateFormat } from '@/filters/date-filter';
import { sumTotal, sumVat, calcVat } from '@/functions/calc-functions';
import { parseInt } from 'lodash';

type ExcemptCompany = {
  id: number;
  name: string;
};

type InvoiceSorting = 'orderNumber' | 'companyName' | 'status' | 'dueDate' | 'amount' | 'ordersCount' | 'orderNumber-desc' | 'companyName-desc' | 'status-desc' | 'dueDate-desc' | 'amount-desc' | 'ordersCount-desc' | 'createdAt' | 'createdAt-desc';

type OrderSorting = 'total' | 'total-desc' | 'earliest' | 'earliest-desc' | 'invoiceable' | 'invoiceable-desc' |
  'orders' | 'orders-desc';

export default defineComponent({

  components: {
    Pagination
  },

  setup(_, context) {

    const getFirstOrder = (companyOrders: CompanyOrders) => {
      return [...companyOrders.orders]
        .sort((a, b) => IsPostingOrder(a) && IsPostingOrder(b) ? moment(a.starts).valueOf() - moment(b.starts).valueOf() : a.id - b.id)[0];
    }

    const getFirstPostingOrder = (companyOrders: CompanyOrders) => {
      return [...FilterPostingOrders([...companyOrders.orders]), ...FilterCandidatePostingOrders([...companyOrders.orders])].sort((a, b) => moment(a.starts).valueOf() - moment(b.starts).valueOf())[0];
    }

    const getTotalPrice = (companyOrders: CompanyOrders) => {
      return sumTotal(companyOrders.orders) + sumVat([...FilterPostingOrders([...companyOrders.orders]), ...FilterCandidatePostingOrders([...companyOrders.orders])], companyOrders.vatAmount) + (companyOrders.paperBilling ? calcVat(26, companyOrders.vatAmount) + 26 : 0);
    }

    const isOrderFinalized = (order: PostingOrder | CandidatePackagePostingOrder) => {
      if (order.orderType === OrderType.Candidate) {
        return moment(order.starts).isBefore(moment());
      }
      return !order.pricePerDay || moment(order.expires).isBefore(moment());
    }

    const getTotalInvoicablePrice = (companyOrders: CompanyOrders) => {
      const orders = [...FilterPostingOrders([...companyOrders.orders]), ...FilterCandidatePostingOrders([...companyOrders.orders])].filter(isOrderFinalized);

      return sumTotal(orders) + sumVat(orders, companyOrders.vatAmount) + (companyOrders.paperBilling ? calcVat(26, companyOrders.vatAmount) + 26 : 0);
    }

    const invoiceOrderNumberSorting = (x: AdminInvoiceView, y: AdminInvoiceView) => {
      return parseInt(x.orderNumber) - parseInt(y.orderNumber);
    };

    const settings = reactive({
      filters: [{ k: 'All', v: null }, ...Object.values(InvoiceStatus).map(v => ({ k: v, v }))],
      filterBy: null as InvoiceStatus | null,
      perPage: 20,
      data: {
        invoicesAll: [] as AdminInvoiceView[],
        orders: [] as CompanyOrders[]
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      sorting: (x: CompanyOrders, y: CompanyOrders) => -1 as number,
      currentSorting: 'total' as OrderSorting,
      invoiceSorting: invoiceOrderNumberSorting,
      currentInvoiceSorting: 'orderNumber' as InvoiceSorting,
    });

    const totalSortAscending = (x: CompanyOrders, y: CompanyOrders) => getTotalPrice(x) - getTotalPrice(y);

    settings.sorting = totalSortAscending;

    const toggleSortTotal = () => {
      if (settings.currentSorting === 'total') {
        settings.currentSorting = 'total-desc';
        settings.sorting = (x, y) => getTotalPrice(y) - getTotalPrice(x);
      }
      else {
        settings.currentSorting = 'total';
        settings.sorting = (x, y) => getTotalPrice(x) - getTotalPrice(y);
      }
    };

    const toggleSortInvoicable = () => {

      if (settings.currentSorting === 'invoiceable') {
        settings.currentSorting = 'invoiceable-desc';
        settings.sorting = (x, y) => getTotalInvoicablePrice(y) - getTotalInvoicablePrice(x);
      }
      else {
        settings.currentSorting = 'invoiceable';
        settings.sorting = (x, y) => getTotalInvoicablePrice(x) - getTotalInvoicablePrice(y);
      }
    };

    const toggleSortEarliestOrder = () => {

      if (settings.currentSorting === 'earliest') {
        settings.currentSorting = 'earliest-desc';
        settings.sorting = (x, y) => {
          const firstX = getFirstOrder(x);

          const firstY = getFirstOrder(y);

          if (IsPostingOrder(firstX) && IsPostingOrder(firstY)) {
            return (new Date(firstY.starts).getTime() - new Date(firstX.starts).getTime());
          }

          return x.id - y.id;
        };
      }
      else {
        settings.currentSorting = 'earliest';
        settings.sorting = (x, y) => {

          const firstX = getFirstOrder(x);

          const firstY = getFirstOrder(y);

          if (IsPostingOrder(firstX) && IsPostingOrder(firstY)) {
            return (new Date(firstX.starts).getTime() - new Date(firstY.starts).getTime());
          }

          return y.id - x.id;
        };
      }
    };

    const toggleSortNumberOfOrders = () => {

      if (settings.currentSorting === 'orders') {
        settings.currentSorting = 'orders-desc';
        settings.sorting = (x, y) => y.orders.length - x.orders.length;
      }
      else {
        settings.currentSorting = 'orders';
        settings.sorting = (x, y) => x.orders.length - y.orders.length;
      }
    };

    const toggleInvoiceOrderNumberSorting = () => {
      if (settings.currentInvoiceSorting === 'orderNumber') {
        settings.currentInvoiceSorting = 'orderNumber-desc';
        settings.invoiceSorting = (x, y) => invoiceOrderNumberSorting(y, x);
      }
      else {
        settings.currentInvoiceSorting = 'orderNumber';
        settings.invoiceSorting = invoiceOrderNumberSorting;
      }
    };

    const toggleInvoiceStatusSorting = () => {
      if (settings.currentInvoiceSorting === 'status') {
        settings.currentInvoiceSorting = 'status-desc';
        settings.invoiceSorting = (x, y) => y.status.localeCompare(x.status, 'en');
      }
      else {
        settings.currentInvoiceSorting = 'status';
        settings.invoiceSorting = (x, y) => y.status.localeCompare(x.status, 'en') * -1;
      }
    };

    const toggleInvoiceAmountSorting = () => {
      if (settings.currentInvoiceSorting === 'amount') {
        settings.currentInvoiceSorting = 'amount-desc';
        settings.invoiceSorting = (x, y) => {
          const x1 = x.status === InvoiceStatus.Canceled ? 0 : x.amount + calcVat(x.amount, x.vat) + (x.paperBilling ? calcVat(26, x.vat) + 26 : 0);

          const y1 = y.status === InvoiceStatus.Canceled ? 0 : y.amount + calcVat(y.amount, y.vat) + (y.paperBilling ? calcVat(26, y.vat) + 26 : 0);

          return y1 - x1;
        };
      }
      else {
        settings.currentInvoiceSorting = 'amount';
        settings.invoiceSorting = (x, y) => {
          const x1 = x.status === InvoiceStatus.Canceled ? 0 : x.amount + calcVat(x.amount, x.vat) + (x.paperBilling ? calcVat(26, x.vat) + 26 : 0);

          const y1 = y.status === InvoiceStatus.Canceled ? 0 : y.amount + calcVat(y.amount, y.vat) + (y.paperBilling ? calcVat(26, y.vat) + 26 : 0);

          return x1 - y1;
        };
      }
    };

    const toggleInvoiceDueDateSorting = () => {
      if (settings.currentInvoiceSorting === 'dueDate') {
        settings.currentInvoiceSorting = 'dueDate-desc';
        settings.invoiceSorting = (x, y) => (y.dueDate && y.status === InvoiceStatus.Sent ? new Date(y.dueDate).getTime() : 0) - (x.dueDate && x.status === InvoiceStatus.Sent ? new Date(x.dueDate).getTime() : 0);
      }
      else {
        settings.currentInvoiceSorting = 'dueDate';
        settings.invoiceSorting = (x, y) => (x.dueDate && x.status === InvoiceStatus.Sent ? new Date(x.dueDate).getTime() : 0) - (y.dueDate && y.status === InvoiceStatus.Sent ? new Date(y.dueDate).getTime() : 0);
      }
    };

    const toggleInvoiceNumberOfOrdersSorting = () => {
      if (settings.currentInvoiceSorting === 'ordersCount') {
        settings.currentInvoiceSorting = 'ordersCount-desc';
        settings.invoiceSorting = (x, y) => y.ordersCount - x.ordersCount;
      }
      else {
        settings.currentInvoiceSorting = 'ordersCount';
        settings.invoiceSorting = (x, y) => x.ordersCount - y.ordersCount;
      }
    };

    const toggleInvoiceCreatedAtSorting = () => {
      if (settings.currentInvoiceSorting === 'createdAt') {
        settings.currentInvoiceSorting = 'createdAt-desc';
        settings.invoiceSorting = (x, y) => new Date(y.createdAt).getTime() - new Date(x.createdAt).getTime();
      }
      else {
        settings.currentInvoiceSorting = 'createdAt';
        settings.invoiceSorting = (x, y) => new Date(x.createdAt).getTime() - new Date(y.createdAt).getTime();
      }
    };


    const model = reactive({
      listInvoices: { startAt: 0, endAt: settings.perPage },
      listOrders: { startAt: 0, endAt: settings.perPage },
      search: '',
      showWithAgreement: null as boolean | null
    });

    // LocalStorage key for persisting filter selection
    const AGREEMENT_FILTER_KEY = 'admin-billing-agreement-filter';

    // Convert string value from LocalStorage to boolean | null
    const stringToFilterValue = (value: string | null): boolean | null => {
      if (value === 'true') return true;
      if (value === 'false') return false;
      return null;
    };

    // Convert boolean | null to string for LocalStorage
    const filterValueToString = (value: boolean | null): string => {
      if (value === true) return 'true';
      if (value === false) return 'false';
      return 'all';
    };

    // Load filter from LocalStorage
    const loadAgreementFilterFromStorage = (): void => {
      const stored = localStorage.getItem(AGREEMENT_FILTER_KEY);
      model.showWithAgreement = stringToFilterValue(stored);
    };

    // Save filter to LocalStorage
    const saveAgreementFilterToStorage = (): void => {
      localStorage.setItem(AGREEMENT_FILTER_KEY, filterValueToString(model.showWithAgreement));
    };

    // Helper function to check if company has a valid billing profile
    const hasBillingProfile = (companyOrders: CompanyOrders): boolean => companyOrders.billingProfileId > 0;

    const isSendingToFortnox = reactive({} as Record<number, boolean>);

    const computedOrders = computed(() => settings.data.orders.sort(settings.sorting).slice(model.listOrders.startAt, model.listOrders.endAt));

    const filterInvoice = (arr: AdminInvoiceView[], status: InvoiceStatus) => arr.filter(x => x.status === status);

    const searchInvoice = (term: string) => (x: AdminInvoiceView) => x.companyName.toLowerCase().includes(term.toLowerCase()) || x.companyOrgNr.toLowerCase().includes(term.toLowerCase());

    const invoices = computed(() => {
      let list = settings.data.invoicesAll;

      if (model.search && model.search.length > 0) {
        list = list.filter(searchInvoice(model.search));
      }

      if (settings.filterBy !== null) {
        list = filterInvoice(list, settings.filterBy);
      }

      return list.sort(settings.invoiceSorting).slice(model.listInvoices.startAt, model.listInvoices.endAt);
    });

    const getInvoiceListChunk = (args: Partial<ListArgs>) => Object.assign(model.listInvoices, { ...args });

    const getOrderListChunk = (args: Partial<ListArgs>) => Object.assign(model.listOrders, { ...args });

    const getInvoicesAll = async () => {

      const response = await axios.get<AdminInvoiceView[]>(SettingsContext.getApiUrl('getAllInvoices'));

      settings.data.invoicesAll = response.data;
    }

    const getInvoicesNotBilled = async () => {

      const response = await axios.get<CompanyOrders[]>(SettingsContext.getApiUrl('getAllOrders'), {
        params: {
          showWithAgreement: model.showWithAgreement
        }
      });

      settings.data.orders = response.data.sort((a, b) => {
        const firstA = getFirstOrder(a);

        const firstB = getFirstOrder(b);

        if (IsPostingOrder(firstA) && IsPostingOrder(firstB)) {
          return moment(firstA.starts).valueOf() - moment(firstB.starts).valueOf();
        }

        return a.id - b.id;
      });
    }

    const onShowWithAgreementChanged = async () => {
      saveAgreementFilterToStorage();
      model.listOrders = { startAt: 0, endAt: settings.perPage };
      await getInvoicesNotBilled();
    }

    onMounted(async () => {

      if (isAdmin() || hasRole('Economy')) {
        loadAgreementFilterFromStorage();

        await getInvoicesNotBilled();

        await getInvoicesAll();
      }
    });

    const requestForInvoice = async (companyOrders: CompanyOrders) => {
      const { id: companyId, billingProfileId, invoiceCreationPeriod, lastInvoiceCreatedAt, companyHasSubscription } = companyOrders;
      
      // Check if company has a valid billing profile
      if (!hasBillingProfile(companyOrders)) {
        await alertDialogue(
          i18n.t('pages.admin-billing.dialogue.request-invoice') as string,
          i18n.t('pages.admin-billing.dialogue.error-no-billing-profile') as string,
          ModalIconType.error
        );
        return;
      }
      
      try {
        let hasAskedPeriodConfirmation = false;

        if (invoiceCreationPeriod !== InvoiceCreationPeriod.none && lastInvoiceCreatedAt) {
          const lastDate = moment(lastInvoiceCreatedAt);
          let nextExpectedDate: moment.Moment;

          switch (invoiceCreationPeriod) {
            case InvoiceCreationPeriod.weekly:
              nextExpectedDate = lastDate.clone().add(1, 'week');
              break;
            case InvoiceCreationPeriod.semiMonthly:
              nextExpectedDate = lastDate.clone().add(2, 'weeks');
              break;
            case InvoiceCreationPeriod.monthly:
              nextExpectedDate = lastDate.clone().add(1, 'month');
              break;
            case InvoiceCreationPeriod.quarterly:
              nextExpectedDate = lastDate.clone().add(3, 'months');
              break;
            default:
              nextExpectedDate = lastDate.clone();
          }

          if (moment().isBefore(nextExpectedDate)) {
            hasAskedPeriodConfirmation = true;
            const confirmed = await confirmDialogue(
              i18n.t('pages.admin-billing.dialogue.request-invoice') as string,
              i18n.t('pages.admin-billing.dialogue.request-invoice-too-soon', { lastInvoiceDate: DateFormat(lastInvoiceCreatedAt) }) as string
            );
            if (!confirmed) return;
          }
        }

        if (!hasAskedPeriodConfirmation && companyHasSubscription) {
          const confirmed = await confirmDialogue(
            i18n.t('pages.admin-billing.dialogue.request-invoice') as string,
            i18n.t('pages.admin-billing.dialogue.request-invoice-has-subscription') as string
          );
          if (!confirmed) return;
        }

        await axios.post(SettingsContext.getApiUrl('RequestForInvoice'), { id: companyId, billingProfileId });

        alertDialogue(i18n.t('pages.admin-billing.dialogue.request-invoice') as string, i18n.t('pages.admin-billing.dialogue.requested-invoice') as string).then(() => {

          getInvoicesNotBilled();

          getInvoicesAll();
        });
      }
      catch (e) {
        const errors = axios.isAxiosError(e) && e.response?.data?.errors
          ? e.response.data.errors as Record<string, string[]>
          : null;

        const message = errors
          ? Object.values(errors).flat().join(' ')
          : null;

        if (message) {
          await alertDialogue(i18n.t('pages.admin-billing.dialogue.request-invoice') as string, message, ModalIconType.error);
        } else {
          await generalError(false);
        }
      }
    }

    const sendInvoiceToFortnox = async (invoice: AdminInvoiceView) => {
      try {
        isSendingToFortnox[invoice.id] = true;

        const model = { id: invoice.id };

        const response = await axios.post<AdminInvoiceView>(SettingsContext.getApiUrl('sendInvoiceToFortnox'), model);

        const updatedInvoice = response.data;
        const targetInvoiceIndex = settings.data.invoicesAll.findIndex(x => x.id === invoice.id);

        if (targetInvoiceIndex !== -1) {
          settings.data.invoicesAll.splice(targetInvoiceIndex, 1, { ...settings.data.invoicesAll[targetInvoiceIndex], ...updatedInvoice });
        }
      }
      catch {
        await generalError(false);
      }
      finally {
        isSendingToFortnox[invoice.id] = false;
      }
    }

    const invoiceNavigate = (companyId: number, invoiceId: number) => {

      return context.root.$router.push({
        name: 'Admin Company Overview',
        params: { companyId: companyId.toString(), invoiceId: invoiceId.toString() }
      });
    }

    return {

      model,

      settings,

      invoices,

      formatAmount,

      DateFormat,

      InvoiceStatus,

      getInvoiceListChunk,

      getOrderListChunk,

      requestForInvoice,

      sendInvoiceToFortnox,

      isSendingToFortnox,

      invoiceNavigate,

      getFirstOrder,

      getFirstPostingOrder,

      getTotalPrice,

      getTotalInvoicablePrice,

      calcVat,

      toggleSortTotal,

      toggleSortInvoicable,

      toggleSortEarliestOrder,

      toggleSortNumberOfOrders,

      computedOrders,

      toggleInvoiceOrderNumberSorting,

      toggleInvoiceStatusSorting,

      toggleInvoiceAmountSorting,

      toggleInvoiceDueDateSorting,

      toggleInvoiceNumberOfOrdersSorting,

      toggleInvoiceCreatedAtSorting,

      onShowWithAgreementChanged,

      hasBillingProfile
    };
  }
})
